import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SiteConfigProvider } from './context/SiteConfigContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { ThemeProvider } from './context/ThemeContext';
import { FxRateProvider } from './context/FxRateContext';
import { MaintenanceProvider, useMaintenance } from './context/MaintenanceContext';
import { MaintenancePage } from './components/MaintenancePage';
import { ProtectedRoute, PublicRoute, PublicOnlyRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import { ImagePreviewProvider } from './components/ImagePreview';
import { InstallAppButton } from './components/InstallAppButton';
import { captureReferralFromUrl } from './utils/referralTracker';

// Pages
import { LandingPage } from './components/LandingPage';
import { HomeRoute } from './components/HomeRoute';
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from './components/AuthPages';
import { MemberDashboard } from './components/MemberDashboard';
import { AdminDashboard, AdminUsersPage, AdminPortfolioPage } from './components/AdminPages';
import { AdminBatchUploadMembersPage } from './components/AdminBatchUploadMembersPage';
import { CompleteProfilePage } from './components/CompleteProfilePage';
import { WalletPage, ReferralsPage } from './components/WalletAndReferrals';
import { PricingPage } from './components/ServicesAndPricing';
import { AdminSettingsPage } from './components/AdminSettingsPage';
import { AdminServicesManagement, AdminPlansManagement } from './components/AdminContentManagement';
import { AdminCMSPage } from './components/AdminCMSPage';
import { AdminMediaLibrary } from './components/AdminMediaLibrary';
import { AdminWithdrawalsPage } from './components/AdminWithdrawalsPage';
import { AdminAuditLogsPage } from './components/AdminAuditLogsPage';
import { AdminAnalyticsPage } from './components/AdminAnalyticsPage';
import { AdminReportsPage } from './components/AdminReportsPage';
import { AdminMarketingAutopilotPage } from './components/AdminMarketingAutopilotPage';
import AdminTrainingPage from './components/AdminTrainingPage';
import { AdminStockCertificatesPage } from './components/AdminStockCertificatesPage';
import { AdminMerchantsPage } from './components/AdminMerchantsPage';
import { AdminRolesPage } from './components/AdminRolesPage';
import { CheckoutSuccessPage } from './components/CheckoutSuccessPage';
import { MemberProfilePage } from './components/MemberProfilePage';
import { MerchantDashboard } from './components/MerchantDashboard';
import { MerchantScanPage } from './components/MerchantScanPage';
import { MerchantTransactionsPage } from './components/MerchantTransactionsPage';
import { AdminTransactionsPage } from './components/AdminTransactionsPage';
import { MerchantPartnersPage } from './components/MerchantPartnersPage';
import { MerchantPartnerDetailPage } from './components/MerchantPartnerDetailPage';
import { MRODashboard } from './components/MRODashboard';
import { MembershipCardPage } from './components/MembershipCardPage';
import { VerifyMembershipPage } from './components/VerifyMembershipPage';
import { VerifyEmailPage } from './components/VerifyEmailPage';
import { AdminEmailHealthPage } from './components/admin-email/AdminEmailHealthPage';
import { CMSPublicPage } from './components/CMSPublicPage';
import { MyDiscountsPage } from './components/MyDiscountsPage';
import { MyPlanPage } from './components/MyPlanPage';
import { MerchantApplyPage } from './components/MerchantApplyPage';
import { HelpCenterPage } from './components/HelpCenterPage';
import { StockCertificatePage } from './components/StockCertificatePage';
import { MROCommissionsPage } from './components/MROCommissionsPage';
import { AdminIndustriesPage } from './components/AdminIndustriesPage';
import { AdminCategoriesPage } from './components/AdminCategoriesPage';
import { AdminVerificationFailuresPage } from './components/AdminVerificationFailuresPage';
import { SupportPage } from './components/SupportPage';
import { AdminSupportPage } from './components/AdminSupportPage';

import './index.css';

// Dashboard Router - redirects based on role
function DashboardRouter() {
  const { user, isAdmin } = useAuth();
  
  if (isAdmin) {
    return <Navigate to="/admin" replace />;
  }
  if (user?.role === 'moderator' || user?.role === 'cro') {
    // Staff land on the user list where they can preview member/merchant docs
    return <Navigate to="/admin/users" replace />;
  }
  if (user?.role === 'merchant') {
    return <Navigate to="/merchant" replace />;
  }
  if (user?.role === 'mro') {
    return <Navigate to="/mro" replace />;
  }
  return <MemberDashboard />;
}

function MaintenanceGate({ children }) {
  const { user } = useAuth();
  const { enabled, message, eta } = useMaintenance();
  // Admins bypass entirely; everyone else sees the takeover when ON.
  if (enabled && user?.role !== 'admin') {
    return <MaintenancePage message={message} eta={eta} />;
  }
  return children;
}

/** Captures `?ref=` and `?mro=` query params on every route change so a
 *  referee who clicks a shared link is credited even if they wander before
 *  registering. Persistence: localStorage + cookie, 30-day TTL, last-touch
 *  wins (a newer click overrides an older one). */
function ReferralTracker() {
  const location = useLocation();
  useEffect(() => {
    captureReferralFromUrl(location.search);
  }, [location.search]);
  return null;
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <SiteConfigProvider>
        <FxRateProvider>
        <MaintenanceProvider>
        <MaintenanceGate>
      <WebSocketProvider>
      <ImagePreviewProvider>
        <BrowserRouter>
          <ReferralTracker />
          <Routes>
            {/* Public Routes — blocked for logged-in users, who are pushed
                back into their dashboard. */}
            <Route path="/" element={
              <PublicOnlyRoute><HomeRoute /></PublicOnlyRoute>
            } />
            <Route path="/login" element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            } />
            <Route path="/register/:type" element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            } />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/complete-profile" element={<CompleteProfilePage />} />
            <Route path="/services" element={<Navigate to="/marketplace" replace />} />
            <Route path="/pricing" element={
              <PublicOnlyRoute><CMSPublicPage slug="pricing" /></PublicOnlyRoute>
            } />
            <Route path="/marketplace" element={<MerchantPartnersPage />} />
            <Route path="/marketplace/:merchantId" element={<MerchantPartnerDetailPage />} />
            <Route path="/merchant-partners" element={<Navigate to="/marketplace" replace />} />
            <Route path="/merchant-partners/:merchantId" element={<MerchantPartnerDetailPage />} />
            <Route path="/verify/:membershipId" element={<VerifyMembershipPage />} />

            {/* Protected Member Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardRouter />
              </ProtectedRoute>
            } />
            <Route path="/wallet" element={
              <ProtectedRoute memberOnly>
                <WalletPage />
              </ProtectedRoute>
            } />
            <Route path="/referrals" element={
              <ProtectedRoute memberOnly>
                <ReferralsPage />
              </ProtectedRoute>
            } />
            <Route path="/my-discounts" element={
              <ProtectedRoute memberOnly>
                <MyDiscountsPage />
              </ProtectedRoute>
            } />
            <Route path="/my-plan" element={
              <ProtectedRoute memberOnly>
                <MyPlanPage />
              </ProtectedRoute>
            } />
            <Route path="/merchant/apply" element={
              <ProtectedRoute memberOnly>
                <MerchantApplyPage />
              </ProtectedRoute>
            } />
            <Route path="/my-bookings" element={
              <ProtectedRoute memberOnly>
                <MyDiscountsPage />
              </ProtectedRoute>
            } />
            <Route path="/help" element={
              <ProtectedRoute>
                <HelpCenterPage />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <MemberProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/merchant" element={
              <ProtectedRoute allowedRoles={['merchant']}>
                <MerchantDashboard />
              </ProtectedRoute>
            } />
            <Route path="/merchant/scan" element={
              <ProtectedRoute allowedRoles={['merchant']}>
                <MerchantScanPage />
              </ProtectedRoute>
            } />
            <Route path="/merchant/transactions" element={
              <ProtectedRoute allowedRoles={['merchant']}>
                <MerchantTransactionsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/transactions" element={
              <ProtectedRoute>
                <AdminTransactionsPage />
              </ProtectedRoute>
            } />
            <Route path="/mro" element={
              <ProtectedRoute allowedRoles={['mro']}>
                <MRODashboard />
              </ProtectedRoute>
            } />
            <Route path="/membership-card" element={
              <ProtectedRoute memberOnly>
                <MembershipCardPage />
              </ProtectedRoute>
            } />
            <Route path="/stocks" element={
              <ProtectedRoute memberOnly>
                <StockCertificatePage />
              </ProtectedRoute>
            } />
            <Route path="/mro/commissions" element={
              <ProtectedRoute allowedRoles={['mro']}>
                <MROCommissionsPage />
              </ProtectedRoute>
            } />
            <Route path="/support" element={
              <ProtectedRoute>
                <SupportPage />
              </ProtectedRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute staffOnly>
                <AdminUsersPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/members/batch-upload" element={
              <ProtectedRoute adminOnly>
                <AdminBatchUploadMembersPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/portfolio" element={
              <ProtectedRoute adminOnly>
                <AdminPortfolioPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/settings" element={
              <ProtectedRoute adminOnly>
                <AdminSettingsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/services" element={
              <ProtectedRoute adminOnly>
                <AdminServicesManagement />
              </ProtectedRoute>
            } />
            <Route path="/admin/plans" element={
              <ProtectedRoute adminOnly>
                <AdminPlansManagement />
              </ProtectedRoute>
            } />
            <Route path="/admin/cms" element={
              <ProtectedRoute adminOnly>
                <AdminCMSPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/media" element={
              <ProtectedRoute adminOnly>
                <AdminMediaLibrary />
              </ProtectedRoute>
            } />
            <Route path="/admin/withdrawals" element={
              <ProtectedRoute adminOnly>
                <AdminWithdrawalsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/audit-logs" element={
              <ProtectedRoute adminOnly>
                <AdminAuditLogsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/analytics" element={
              <ProtectedRoute adminOnly>
                <AdminAnalyticsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/email-health" element={
              <ProtectedRoute adminOnly>
                <AdminEmailHealthPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing" element={
              <ProtectedRoute adminOnly>
                <AdminMarketingAutopilotPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/reports" element={
              <ProtectedRoute staffOnly>
                <AdminReportsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/training" element={
              <ProtectedRoute adminOnly>
                <AdminTrainingPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/stock-certificates" element={
              <ProtectedRoute staffOnly>
                <AdminStockCertificatesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/roles" element={
              <ProtectedRoute adminOnly>
                <AdminRolesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/merchants" element={
              <ProtectedRoute staffOnly>
                <AdminMerchantsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/industries" element={
              <ProtectedRoute adminOnly>
                <AdminIndustriesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/marketplace/categories" element={
              <ProtectedRoute adminOnly>
                <AdminCategoriesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/verification-failures" element={
              <ProtectedRoute adminOnly>
                <AdminVerificationFailuresPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/support" element={
              <ProtectedRoute staffOnly>
                <AdminSupportPage />
              </ProtectedRoute>
            } />

            {/* CMS Dynamic Pages — public marketing content; hide from logged-in users. */}
            <Route path="/page/insurance" element={<CMSPublicPage slug="insurance" />} />
            <Route path="/page/:slug" element={
              <PublicOnlyRoute><CMSPublicPage /></PublicOnlyRoute>
            } />

            {/* Checkout */}
            <Route path="/checkout/success" element={
              <ProtectedRoute>
                <CheckoutSuccessPage />
              </ProtectedRoute>
            } />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
        <InstallAppButton />
      </ImagePreviewProvider>
      </WebSocketProvider>
        </MaintenanceGate>
        </MaintenanceProvider>
        </FxRateProvider>
      </SiteConfigProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
